/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import screenfull from 'screenfull';
import findIndex from 'lodash/findIndex';
import Slider from 'components/Slider/Slider';
import useBreakpoints from 'utils/breakpoints';
import Arrow from 'components/Icons/Arrow/Arrow';
import FullScreenGallery from 'components/FullScreenGallery/Gallery/Gallery';
import Slide from './Slide/Slide';
import './Gallery.scss';

/**
 * This is the Gallery component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Gallery({
  target,
  show,
  all,
  prev,
  next,
  exit,
  maximize,
  filter,
  slides,
  settings,
  dynamicHeight,
}) {
  const fullscreenTarget = document.getElementById(target);
  const breakpoint = useBreakpoints({
    tablet: 760,
    desktop: 1024,
  });
  const container = useRef(null);
  const slider = useRef(null);

  const [active, setActive] = useState(false);
  const [current, setCurrent] = useState(false);
  const [selected, setSelected] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [scroll, setScroll] = useState(0);

  useEffect(() => {
    function onFullScreenchange({ target }) {
      if (target === fullscreenTarget) {
        setFullscreen(screenfull.isFullscreen);
      }
    }
    if (screenfull.isEnabled) {
      screenfull.on('change', onFullScreenchange);
      return function cleanup() {
        screenfull.on('off', onFullScreenchange);
      };
    }
  });

  useEffect(() => {
    if (fullscreenTarget) {
      fullscreenTarget.classList.add('full-screen-gallery-container');
    }

    return function cleanup() {
      if (fullscreenTarget) {
        fullscreenTarget.classList.remove('full-screen-gallery-container');
      }
    };
  }, [fullscreenTarget, target]);

  useEffect(() => {
    if (fullscreen) {
      if (fullscreenTarget) {
        fullscreenTarget.classList.add('active');
      }
      document.documentElement.classList.add('full-screen-gallery-active');
    } else {
      if (fullscreenTarget) {
        fullscreenTarget.classList.remove('active');
      }
      document.documentElement.classList.remove('full-screen-gallery-active');
    }

    return function cleanup() {
      if (fullscreenTarget) {
        fullscreenTarget.classList.remove('active');
      }
      document.documentElement.classList.remove('full-screen-gallery-active');
    };
  }, [fullscreenTarget, fullscreen]);

  useEffect(() => {
    if (!fullscreen && container.current) {
      if (typeof window.scrollTo === 'function') {
        window.scrollTo(0, scroll);
      }
    }
  }, [fullscreen, scroll]);

  function renderFilter() {
    if (!Object.keys(filter).length) return null;
    if (breakpoint === null) {
      return (
        <>
          <label htmlFor="filter" aria-labelledby="filter">
            {show}
          </label>
          <div className="filter-select">
            <select
              id="filter"
              value={selected}
              onChange={({ target: { value } }) => {
                setSelected(value);
              }}
            >
              <option value="">{all}</option>
              {Object.keys(filter).map(key => (
                <option key={key} value={key}>
                  {filter[key]}
                </option>
              ))}
            </select>
          </div>
        </>
      );
    }

    return (
      <>
        <label htmlFor="filter" aria-labelledby="filter">
          {show}
        </label>
        <div id="filter" className="filter-buttons">
          <button
            className={classnames('all', { selected: selected === '' })}
            title={all}
            onClick={() => {
              setSelected('');
            }}
          >
            {all}
          </button>
          {Object.keys(filter).map(key => (
            <button
              key={key}
              className={classnames({ selected: selected === key })}
              title={filter[key]}
              onClick={() => {
                setSelected(key);
              }}
            >
              {filter[key]}
            </button>
          ))}
        </div>
      </>
    );
  }

  const filteredSlides = slides.filter(({ tags }) => {
    if (selected) {
      return tags.indexOf(selected) !== -1;
    }
    return true;
  });

  function getSettings() {
    if (settings === null) {
      return {};
    }

    if (typeof settings[breakpoint] === 'object') {
      return settings[breakpoint];
    }

    if (typeof settings.default === 'object') {
      return settings.default;
    }

    return {};
  }

  if (fullscreen) {
    return createPortal(
      <FullScreenGallery
        close={exit}
        prev={prev}
        next={next}
        images={slides.map(({ image, ...props }) => ({
          ...props,
          image: image ? image.full : undefined,
        }))}
        current={current}
        onExit={() => {
          if (screenfull.isEnabled) {
            screenfull.exit(fullscreenTarget);
          } else {
            setFullscreen(false);
          }
        }}
      />,
      fullscreenTarget
    );
  }

  return (
    <aside ref={container} className="gallery">
      {(() => {
        if (slides.length > 1) {
          return <div className="page-padding filter">{renderFilter()}</div>;
        }
        return null;
      })()}

      {(() => {
        if (slides.length > 1) {
          return (
            <nav className={classnames('navigation', { active })}>
              <button
                onClick={() => {
                  slider.current.prev();
                }}
              >
                <Arrow left title={prev} />
              </button>
              <button
                onClick={() => {
                  slider.current.next();
                }}
              >
                <Arrow right title={next} />
              </button>
            </nav>
          );
        }
        return null;
      })()}

      <Slider ref={slider} {...getSettings()} onActive={setActive}>
        {filteredSlides.map(({ id, image, ...props }) => (
          <Slide
            key={id}
            maximize={maximize}
            dynamicHeight={dynamicHeight}
            {...props}
            image={image ? image.thumb : undefined}
            focalPoint={image ? image.focalPoint : undefined}
            onClick={() => {
              const index = findIndex(
                slides,
                ({ id: slideId }) => slideId === id
              );
              setCurrent(index);
              setScroll(document.documentElement.scrollTop);
              if (screenfull.isEnabled) {
                screenfull.request(fullscreenTarget);
              } else {
                setFullscreen(true);
              }
            }}
          />
        ))}
      </Slider>
    </aside>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Gallery.propTypes = {
  target: PropTypes.string.isRequired,
  show: PropTypes.string.isRequired,
  all: PropTypes.string.isRequired,
  prev: PropTypes.string.isRequired,
  next: PropTypes.string.isRequired,
  exit: PropTypes.string.isRequired,
  maximize: PropTypes.string.isRequired,
  filter: PropTypes.object.isRequired,
  slides: PropTypes.arrayOf(PropTypes.object).isRequired,
  settings: PropTypes.object.isRequired,
  dynamicHeight: PropTypes.bool,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Gallery.defaultProps = {
  target: 'fullscreen-gallery',
  show: 'Show:',
  all: 'All',
  prev: 'Previous',
  next: 'Next',
  exit: 'Exit',
  maximize: 'Maximize',
  filter: {},
  slides: [],
  settings: {
    desktop: {
      show: 3,
      slide: 3,
    },
    tablet: {
      show: 2,
      slide: 2,
    },
  },
  dynamicHeight: false,
};

export default Gallery;
